<script setup lang="ts">
// 1:1 Port von club-offering/src/app/core/components/header.
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import KgrMenuLanguageSwitch from '@shared/components/KgrMenuLanguageSwitch.vue';
import { apiUrl } from '@shared/http/client';
import { loadLocale } from '@shared/i18n';
import { useAuthImage } from '@shared/composables/useAuthImage';
import { useOrganizationStore } from '@shared/stores/organization';
import type { Organization } from '@shared/stores/organization';

// Genau die fuenf Locales, die angular.json fuer diese App baut — nicht die elf der anderen Apps.
const LOCALES = ['de', 'en', 'es', 'tr', 'uk'];

const route = useRoute();
const router = useRouter();
const orgStore = useOrganizationStore();

// Angulars `organization$` faellt nie auf null zurueck, sobald ein Verein geladen ist:
// `loginOrganization$` filtert die Null-Emission eines Logouts weg (`filter(id => !!id)`,
// organization.service.ts:53-58) und `shareReplay(1)` haelt das zuletzt geladene Profil. Vues
// `userStore.logout()` setzt den Store dagegen auf den Vor-Login-Stand zurueck — den es in dieser
// App nicht gibt (weder `organizationProfile` noch `platformOrganization` sind gesetzt). Ohne
// diesen Riegel verschwaende der Kopfbereich nach dem Passwort-Setzen in der Gast-Registrierung
// Logo und Vereinsnamen. Der Riegel muss HIER sitzen und nicht im Store: wuerde das Profil erst
// zurueckgesetzt und dann neu gesetzt, liefe `useAuthImage` erneut los — ohne Token, also ohne Logo.
const organization = ref<Organization | null>(null);
watch(
    () => orgStore.organization,
    (value) => {
        if (value) {
            organization.value = value;
        }
    },
    { immediate: true },
);
const activeLocale = computed(() => (route.params.locale as string | undefined) ?? 'de');

// Angulars `organization.logoApiUrl | apiImageUrl` — Bearer-geschuetzt, deshalb kein blankes
// <img src>. `logoApiUrl` kommt ohne Host; ohne `apiUrl()` laedt der Browser gegen den Dev-Server.
const logoUrl = computed(() => (organization.value?.hasLogo ? apiUrl(organization.value.logoApiUrl) : null));
const logoObjectUrl = useAuthImage(logoUrl);

const clubLink = computed(() => `/${activeLocale.value}/${organization.value?.id ?? ''}`);

// Angular ersetzt hier die document.location (`/angebote/xx/` → `/angebote/<locale>/`), weil die
// Sprache dort ein BUILD ist. In Vue ist sie ein Routen-Segment, also navigiert der Router —
// derselbe Weg wie in den vier migrierten Apps. Ein harter Reload wuerde hier zusaetzlich das
// Gast-Token verwerfen, das bewusst nur im Speicher liegt.
async function setLocale(localeId: string) {
    await loadLocale(localeId);
    await router.push(route.fullPath.replace(new RegExp(`^/${route.params.locale as string}/`), `/${localeId}/`));
}
</script>

<template>
    <!--
        Das Wurzel-Element rendert IMMER. In Angular steht `@if (organization)` im TEMPLATE von
        kgr-header, das Host-Element existiert also unabhaengig davon und traegt
        `height: var(--kgr-pageLayout-header-height)` — es belegt 80px auch ohne Inhalt. Ein
        `v-if` auf der Wurzel wuerde diesen Platz verschlucken und die ganze Seite um 80px
        nach oben ziehen (APP_MIGRATE.md §11, ":host-CSS aendert den Layout-Kontext").
    -->
    <div class="appHeader">
        <div
            v-if="organization"
            class="kgr-region kgr-postfix kgr-postfix--center kgr-postfix--gutters"
        >
            <RouterLink
                v-if="logoObjectUrl"
                :to="clubLink"
                class="kgr-postfix__fixed"
            >
                <img
                    :src="logoObjectUrl"
                    class="logo"
                    alt="Logo"
                />
            </RouterLink>
            <div class="kgr-postfix__fluid">
                <RouterLink :to="clubLink">
                    <h1 class="kgr-headline kgr-headline--h2">
                        {{ organization.name }}
                    </h1>
                </RouterLink>
            </div>
            <div class="kgr-postfix__fixed kgr-hiddenFullscreen">
                <KgrMenuLanguageSwitch
                    :locales="LOCALES"
                    :active-locale="activeLocale"
                    @locale-changed="setLocale"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Angular: :host { display: block; height: var(--kgr-pageLayout-header-height) } */
.appHeader {
    display: block;
    height: var(--kgr-pageLayout-header-height);
}

.logo {
    max-width: 120px;
    max-height: 40px;
}
</style>
